import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { transacoesApi } from '../../api/endpoints'
import { Button } from '../../components/ui/Button'
import { LoadingState, EmptyState } from '../../components/ui/EmptyState'
import { Modal } from '../../components/ui/Modal'
import { useToast } from '../../components/ui/Toast'
import { formatMoney, formatDate } from '../../lib/utils'

/** Botão + modal pra fundir um lançamento vindo da Pluggy com um manual parecido (mesma conta/cartão,
 * valor e data próximos): o manual herda o vínculo com a Pluggy e o duplicado é apagado. */
export function BotaoFundirLancamento({ transacaoId, onFundido }: { transacaoId: string; onFundido: () => void }) {
  const [aberto, setAberto] = useState(false)
  const toast = useToast()

  const { data: candidatos = [], isLoading } = useQuery({
    queryKey: ['candidatos-fusao', transacaoId],
    queryFn: () => transacoesApi.listarCandidatosFusao(transacaoId),
    enabled: aberto,
  })

  const fundir = useMutation({
    mutationFn: (idManual: string) => transacoesApi.fundir(transacaoId, idManual),
    onSuccess: () => { toast.success('Lançamentos fundidos.'); setAberto(false); onFundido() },
    onError: (e: Error) => toast.error(e.message),
  })

  return (
    <>
      <Button type="button" variant="light" icon="ri-link" onClick={() => setAberto(true)}>Vincular a outro lançamento</Button>
      <Modal open={aberto} onClose={() => setAberto(false)} title="Vincular a outro lançamento">
        <p className="mb-3 text-13 text-muted">
          Escolha o lançamento manual que já representa isso. Este (o da Pluggy) fica com a data e o valor exatos do banco,
          herdando a descrição, categoria e observação do manual escolhido — que é removido.
        </p>
        {isLoading ? <LoadingState /> : candidatos.length === 0 ? (
          <EmptyState icon="ri-search-line" message="Nenhum lançamento manual parecido (mesma conta/cartão, valor e data próximos)." />
        ) : (
          <div className="space-y-2">
            {candidatos.map((c) => (
              <button
                key={c.id} type="button" disabled={fundir.isPending} onClick={() => fundir.mutate(c.id)}
                className="flex w-full items-center justify-between gap-3 rounded border border-border/70 px-3 py-2 text-left transition-colors hover:border-primary/50 disabled:opacity-60"
              >
                <div className="min-w-0">
                  <p className="truncate font-medium text-white">{c.description}</p>
                  <p className="text-2xs text-muted">{formatDate(c.date)}{c.categoriaNome ? ` · ${c.categoriaNome}` : ''}</p>
                </div>
                <span className="whitespace-nowrap font-semibold text-white">{formatMoney(c.amount)}</span>
              </button>
            ))}
          </div>
        )}
      </Modal>
    </>
  )
}
